// Zone-Specific Task Timing and Customization Engine
import { hardinessZones } from './hardinessZones';

// Task timing adjustments by zone
export const zoneTimingAdjustments = {
  // Northern zones (3a-4b) - Later spring, earlier fall
  '3a': { springShift: +8, fallShift: -8, growingSeason: 'short' },
  '3b': { springShift: +6, fallShift: -6, growingSeason: 'short' },
  '4a': { springShift: +4, fallShift: -4, growingSeason: 'short' },
  '4b': { springShift: +2, fallShift: -2, growingSeason: 'medium' },
  
  // Moderate zones (5a-6b) - Standard timing
  '5a': { springShift: +1, fallShift: -1, growingSeason: 'medium' },
  '5b': { springShift: 0, fallShift: 0, growingSeason: 'medium' }, // Massachusetts baseline
  '6a': { springShift: -1, fallShift: +1, growingSeason: 'medium' },
  '6b': { springShift: -2, fallShift: +2, growingSeason: 'long' },
  
  // Southern zones (7a-10b) - Earlier spring, later fall
  '7a': { springShift: -3, fallShift: +3, growingSeason: 'long' },
  '7b': { springShift: -4, fallShift: +4, growingSeason: 'long' },
  '8a': { springShift: -6, fallShift: +6, growingSeason: 'extended' },
  '8b': { springShift: -8, fallShift: +8, growingSeason: 'extended' },
  '9a': { springShift: -10, fallShift: +10, growingSeason: 'yearround' },
  '9b': { springShift: -12, fallShift: +12, growingSeason: 'yearround' },
  '10a': { springShift: -14, fallShift: +14, growingSeason: 'yearround' },
  '10b': { springShift: -16, fallShift: +16, growingSeason: 'yearround' }
};

// Grass type priorities by zone
export const zoneGrassTypePriorities = {
  // Cool-season grass zones
  '3a': ['Fine Fescue'],
  '3b': ['Fine Fescue', 'Perennial Ryegrass'],
  '4a': ['Kentucky Bluegrass', 'Fine Fescue'],
  '4b': ['Kentucky Bluegrass', 'Fine Fescue', 'Perennial Ryegrass'],
  '5a': ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Fine Fescue'],
  '5b': ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue'],
  '6a': ['Tall Fescue', 'Kentucky Bluegrass', 'Perennial Ryegrass'],
  '6b': ['Tall Fescue', 'Kentucky Bluegrass', 'Zoysia'],
  
  // Transition zones
  '7a': ['Tall Fescue', 'Zoysia', 'Bermuda'],
  '7b': ['Tall Fescue', 'Zoysia', 'Bermuda'],
  
  // Warm-season grass zones
  '8a': ['Zoysia', 'Bermuda', 'Centipede'],
  '8b': ['Bermuda', 'Zoysia', 'St. Augustine'],
  '9a': ['Bermuda', 'St. Augustine', 'Zoysia'],
  '9b': ['St. Augustine', 'Bermuda', 'Bahia'],
  '10a': ['St. Augustine', 'Bahia', 'Seashore Paspalum'],
  '10b': ['St. Augustine', 'Seashore Paspalum']
};

// Zone-specific task modifications
export const zoneTaskModifications = {
  // Pre-emergent timing based on soil temperature
  preEmergent: {
    '3a': { timing: 'Early May', note: 'Apply when soil reaches 50°F consistently' },
    '3b': { timing: 'Late April', note: 'Monitor soil temperature closely' },
    '4a': { timing: 'Mid April', note: 'Critical for crabgrass prevention' },
    '4b': { timing: 'Early April', note: 'Apply before forsythia blooms' },
    '5a': { timing: 'Late March', note: 'Standard Massachusetts timing' },
    '5b': { timing: 'Mid to Late March', note: 'When soil hits 50°F for 3 days' },
    '6a': { timing: 'Mid March', note: 'Earlier application needed' },
    '6b': { timing: 'Early March', note: 'Watch for early warm spells' },
    '7a': { timing: 'Late February', note: 'Much earlier than northern zones' },
    '7b': { timing: 'Mid February', note: 'Apply before spring growth' },
    '8a': { timing: 'Early February', note: 'Year-round growing season' },
    '8b': { timing: 'Late January', note: 'Multiple applications may be needed' },
    '9a': { timing: 'January', note: 'Continuous growing season' },
    '9b': { timing: 'Year-round', note: 'Split applications recommended' },
    '10a': { timing: 'Year-round', note: 'Monthly applications' },
    '10b': { timing: 'Year-round', note: 'Continuous management needed' }
  },
  
  // Fall overseeding windows
  overseeding: {
    '3a': { timing: 'Late July to Early August', note: 'Very short window' },
    '3b': { timing: 'Early to Mid August', note: 'Limited time before frost' },
    '4a': { timing: 'Mid August', note: 'Allow 6 weeks before frost' },
    '4b': { timing: 'Late August', note: 'Ideal conditions developing' },
    '5a': { timing: 'Late August to Early September', note: 'Perfect timing zone' },
    '5b': { timing: 'Early to Mid September', note: 'Optimal overseeding period' },
    '6a': { timing: 'Mid September', note: 'Extended favorable window' },
    '6b': { timing: 'Late September', note: 'Longer growing season' },
    '7a': { timing: 'Early October', note: 'Cool-season grass areas only' },
    '7b': { timing: 'Mid October', note: 'Limited to tall fescue' },
    '8a': { timing: 'Late October', note: 'Warm-season grass dominant' },
    '8b': { timing: 'November', note: 'Consider warm-season alternatives' },
    '9a': { timing: 'Not recommended', note: 'Focus on warm-season grasses' },
    '9b': { timing: 'Not recommended', note: 'Year-round warm-season care' },
    '10a': { timing: 'Not applicable', note: 'Tropical grass management' },
    '10b': { timing: 'Not applicable', note: 'Continuous warm-season growth' }
  },
  
  // Fertilization schedules
  fertilization: {
    '3a': { 
      schedule: ['Late May', 'Mid July', 'Early September'],
      note: 'Short season, focus on summer strength'
    },
    '4a': { 
      schedule: ['Early May', 'Mid July', 'Early September'],
      note: 'Build resilience for winter'
    },
    '5b': { 
      schedule: ['Early April', 'Late May', 'Early September', 'Late October'],
      note: 'Massachusetts standard schedule'
    },
    '7a': { 
      schedule: ['Early March', 'Late April', 'Early June', 'Early September', 'Late October'],
      note: 'Extended growing season'
    },
    '8b': { 
      schedule: ['February', 'April', 'June', 'August', 'October'],
      note: 'Nearly year-round feeding'
    },
    '9b': { 
      schedule: ['Monthly March-November'],
      note: 'Continuous growth requires regular feeding'
    }
  }
};

// Get zone-specific recommendations for a task
export const getZoneTaskRecommendation = (taskText, zone) => {
  const zoneData = hardinessZones[zone];
  if (!zoneData) return null;

  // Check for pre-emergent tasks
  if (taskText.toLowerCase().includes('pre-emergent') || 
      taskText.toLowerCase().includes('crabgrass preventer')) {
    return zoneTaskModifications.preEmergent[zone];
  }

  // Check for overseeding tasks
  if (taskText.toLowerCase().includes('overseed') || 
      taskText.toLowerCase().includes('seed')) {
    return zoneTaskModifications.overseeding[zone];
  }

  // Check for fertilization tasks
  if (taskText.toLowerCase().includes('fertiliz') || 
      taskText.toLowerCase().includes('feed')) {
    return zoneTaskModifications.fertilization[zone];
  }

  return null;
};

// Filter tasks by grass type relevance
export const filterTasksByGrassType = (tasks, zone) => {
  const relevantGrassTypes = zoneGrassTypePriorities[zone];
  if (!relevantGrassTypes) return tasks;

  return tasks.map(task => {
    // Check if task mentions specific grass types
    const taskText = task.text.toLowerCase();
    const grassTypeRelevance = relevantGrassTypes.some(grassType => 
      taskText.includes(grassType.toLowerCase())
    );

    // Mark tasks as zone-appropriate or add zone-specific notes
    if (grassTypeRelevance || !taskText.includes('grass')) {
      return task; // Keep relevant tasks as-is
    }

    // Add zone-specific note for general grass tasks
    return {
      ...task,
      zoneNote: `Recommended for ${relevantGrassTypes.slice(0, 2).join(' and ')} in your zone`
    };
  });
};

// Get zone-specific monthly adjustments
export const getZoneMonthlyAdjustments = (month, zone) => {
  const timing = zoneTimingAdjustments[zone];
  if (!timing) return { tasks: [], adjustments: [] };

  const adjustments = [];
  
  // Spring adjustments
  if (['March', 'April', 'May'].includes(month) && timing.springShift !== 0) {
    const direction = timing.springShift > 0 ? 'later' : 'earlier';
    const weeks = Math.abs(timing.springShift);
    adjustments.push(`Spring tasks run ${weeks} weeks ${direction} in your zone`);
  }
  
  // Fall adjustments  
  if (['September', 'October', 'November'].includes(month) && timing.fallShift !== 0) {
    const direction = timing.fallShift > 0 ? 'later' : 'earlier';
    const weeks = Math.abs(timing.fallShift);
    adjustments.push(`Fall tasks run ${weeks} weeks ${direction} in your zone`);
  }

  return { adjustments };
};

// Generate zone-specific critical alerts
export const getZoneCriticalAlerts = (zone, currentMonth) => {
  const alerts = [];
  const zoneData = hardinessZones[zone];
  
  if (!zoneData) return alerts;

  // Pre-emergent timing alerts
  if (['February', 'March', 'April'].includes(currentMonth)) {
    const preEmergentTiming = zoneTaskModifications.preEmergent[zone];
    if (preEmergentTiming) {
      alerts.push({
        type: 'critical',
        icon: '🚨',
        title: 'Pre-Emergent Timing Critical',
        message: `Apply pre-emergent ${preEmergentTiming.timing}. ${preEmergentTiming.note}`,
        priority: 'critical'
      });
    }
  }

  // Overseeding window alerts
  if (['July', 'August', 'September', 'October'].includes(currentMonth)) {
    const overseedTiming = zoneTaskModifications.overseeding[zone];
    if (overseedTiming && !overseedTiming.timing.includes('Not')) {
      alerts.push({
        type: 'warning',
        icon: '🌱',
        title: 'Overseeding Window',
        message: `Optimal overseeding time: ${overseedTiming.timing}. ${overseedTiming.note}`,
        priority: 'high'
      });
    }
  }

  return alerts;
};